import { useEffect, useMemo, useRef } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { differenceInMilliseconds } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';

import { isExternalUserAtom } from '../../atoms/atoms';
import {
  practiceImageIdAtom,
  practicePatientIdAtom,
  redirectToSwitchBoardAtom,
} from '../../atoms/externalAtoms';
import { VideaButton } from '../../common-components/elements/VideaButton/VideaButton';
import { VideaTypography } from '../../common-components/elements/VideaTypography/VideaTypography';
import {
  isDirectorUser,
  isMultiPracticeUser,
} from '../../modules/login/login-utils';
import { useShouldHideHuddle } from '../../modules/patientList/useShouldHideHuddle';
import { useGetPractices } from '../../react-query/useGetPractices';
import routes from '../../routes/routes.constants';
import { blueBlack630 } from '../../theme/videa-style-tokens';
import { directorPracticeIdAtom } from '../../utils/hooks/useDirectorPracticeView';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import {
  FeatureFlagType,
  OrganizationFeatureFlagKey,
  useCheckIfSagePractice,
  useFeatureFlag,
} from '../../utils/user';
import { selectedVisitAtom } from '../appHeader/imageViewerMenu/atoms';

import { selectedViewAtom } from './atoms';
import { fetchVideaImageId, fetchVideaPatientId } from './utils';

export const LoadingErrorContainer = styled.div`
  background-color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  .message-container {
    width: 90%;
    max-width: 30em;
    min-width: 280px;
    background-color: white;
    border-radius: 1em;
    padding: 2em 1.5em 1.5em;
    display: flex;
    flex-direction: column;
    gap: 2em;
    align-items: flex-start;

    @media (min-width: 768px) {
      width: 40%;
      padding: 3em 3em 2em;
    }
  }

  .MuiLinearProgress-root {
    width: 100%;
  }
`;

export const ExternalUrlHandler = () => {
  // atoms
  const setIsExternalUser = useSetAtom(isExternalUserAtom);
  const setEstimatedVisitId = useSetAtom(selectedVisitAtom);
  const setRedirectToSwitchBoard = useSetAtom(redirectToSwitchBoardAtom);

  const videaAiView = useAtomValue(selectedViewAtom);

  const [directorPracticeId, setDirectorPracticeId] = useAtom(
    directorPracticeIdAtom,
  );
  const [practicePatientId, setPracticePatientId] = useAtom(
    practicePatientIdAtom,
  );
  const [practiceImageId, setPracticeImageId] = useAtom(practiceImageIdAtom);

  // Track when we're redirecting to prevent error case
  const isRedirecting = useRef(false);

  useEffect(() => {
    return () => {
      // Cleanup: clear practice IDs when component unmounts
      setPracticeImageId('');
      setPracticePatientId('');
    };
  }, [setPracticeImageId, setPracticePatientId]);

  // hooks
  const user = useSelector((state: any) => state?.login?.user);
  const primaryPracticeId = user.primaryPracticeId;
  const { primaryOrganizationId } = user;
  const hideHuddle = useShouldHideHuddle();

  // Get practices for director users who don't have a primaryPracticeId
  const { data: practices } = useGetPractices({});

  // For director users without primaryPracticeId, use the first practice
  const effectivePrimaryPracticeId = useMemo(() => {
    if (primaryPracticeId) {
      return primaryPracticeId;
    }
    if (isDirectorUser(user) && practices?.pages?.[0]?.data?.[0]?.id) {
      return practices.pages[0].data[0].id;
    }
    return null;
  }, [primaryPracticeId, user, practices]);

  const {
    data: videaPatientIdData,
    isInitialLoading: isLoadingVideaPatientId,
    isError: isErrorVideaPatientId,
    failureCount: videaPatientIdFailureCount,
  } = useQuery(
    [
      'get-videa-patient-id',
      practicePatientId,
      effectivePrimaryPracticeId,
      primaryOrganizationId,
    ],

    () => fetchVideaPatientId(practicePatientId, effectivePrimaryPracticeId),
    {
      enabled: !!practicePatientId && !!effectivePrimaryPracticeId,
      retryDelay: 5000,
      retry: 24,
    },
  );

  useEffect(() => {
    if (isSageDeepLinkUser && videaPatientIdData) {
      /* use practiceVideaId to default sage deep link users to the first matching:
          - unified appointment's practice id
          - PMS patient's practice id 
          - imaging patient's practice id
          - Otherwise, default to the vault practice
          */
      setDirectorPracticeId(
        videaPatientIdData.practiceVideaId || user.vaultPracticeVideaId,
      );
    }
    // Set director practice ID for multi-practice users when practiceVideaId is available
    if (isMultiPracticeUser(user) && videaPatientIdData?.practiceVideaId) {
      setDirectorPracticeId(videaPatientIdData.practiceVideaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videaPatientIdData, setPracticePatientId]);

  const { trackEvent } = useTrackEvent();

  const {
    data: videaImageId,
    isInitialLoading: isLoadingVideaImageId,
    isError: isErrorVideaImageId,
    failureCount: videaImageIdFailureCount,
  } = useQuery(
    [
      'get-videa-image-id',
      practicePatientId,
      effectivePrimaryPracticeId,
      primaryOrganizationId,
      practiceImageId,
    ],

    () =>
      fetchVideaImageId(
        practicePatientId,
        effectivePrimaryPracticeId,
        primaryOrganizationId,
        practiceImageId,
      ),
    {
      enabled:
        !!practicePatientId &&
        !!effectivePrimaryPracticeId &&
        !!practiceImageId,
      retryDelay: 5000,
      retry: 24,
    },
  );

  const showOpDashboard = !!useFeatureFlag(
    OrganizationFeatureFlagKey.INSIGHTS_DASHBOARD_ENABLED,
    FeatureFlagType.ORGANIZATION,
  )?.value;

  // variables
  const startTime = useMemo(() => new Date(), []);
  const canViewMultiplePractices = isMultiPracticeUser(user);
  const videaPatientId = videaPatientIdData?.patientId;
  const timeOpen = differenceInMilliseconds(new Date(), startTime);
  const isEmbeddedUser = videaAiView === 'EMBEDDED';
  const patientSuccessImageFailure =
    videaImageIdFailureCount > 0 && videaPatientId;
  const isSageDeepLinkUser =
    user.vaultPracticeVideaId && canViewMultiplePractices;
  const isLoadingSageVaultPractice =
    !user.primaryPracticeId && isSageDeepLinkUser;

  // Handle redirect to uploader only once on mount when no patient data
  useEffect(() => {
    if (!videaPatientId && !practicePatientId) {
      window.location.href = routes.UPLOADER;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only runs once on mount

  if (videaPatientIdFailureCount >= 6 || isErrorVideaPatientId) {
    trackEvent(mixpanelEvents.externalUrlTimeout, {
      reason:
        videaPatientIdFailureCount >= 6
          ? 'patient id max retries exceeded'
          : 'patient id error',
      timeOpen,
    });

    const errorUrl = isEmbeddedUser
      ? `/embedded${routes.PATIENT_MATCH_ERROR}`
      : routes.PATIENT_MATCH_ERROR;
    isRedirecting.current = true;
    return <Redirect to={errorUrl} />;
  }

  let backLink: string = routes.UPLOADER;
  let backButtonText = hideHuddle ? (
    <Trans>All Patients</Trans>
  ) : (
    <Trans>Dashboard</Trans>
  );

  if (patientSuccessImageFailure) {
    backLink = `${routes.ANNOTATOR}/${videaPatientId}`;
    backButtonText = <Trans>Go to Patient</Trans>;
    // If we are a sage user and weren't able to find an appropriate practice other than the vault practice,
    // show back to practice list instead of patient list.
  } else if (
    isSageDeepLinkUser &&
    directorPracticeId &&
    directorPracticeId === user.vaultPracticeVideaId
  ) {
    backLink = routes.SWITCHBOARD;
    backButtonText = <Trans>Back to Practice List</Trans>;
  }

  if (
    isLoadingVideaImageId ||
    isLoadingVideaPatientId ||
    isLoadingSageVaultPractice
  ) {
    return (
      <LoadingErrorContainer>
        <div className="message-container">
          <VideaTypography variant="headingMedium" color={blueBlack630}>
            <Trans>
              Your {patientSuccessImageFailure ? "patient's image" : 'patient'}{' '}
              is{' '}
              {videaImageIdFailureCount || videaPatientIdFailureCount
                ? 'being analyzed'
                : 'loading'}
              . Please wait.
            </Trans>
          </VideaTypography>
          <LinearProgress />
          {(videaImageIdFailureCount >= 2 ||
            videaPatientIdFailureCount >= 2) && (
            <Link
              to={backLink}
              onClick={() => {
                setPracticeImageId('');
                setPracticePatientId('');
                if (
                  isSageDeepLinkUser &&
                  directorPracticeId &&
                  directorPracticeId === user.vaultPracticeVideaId
                ) {
                  setDirectorPracticeId(null);
                  return;
                }

                trackEvent(mixpanelEvents.externalUrlBackToPatientList, {
                  timeOpen,
                });
              }}
            >
              <VideaButton variant="contained">{backButtonText}</VideaButton>
            </Link>
          )}
        </div>
      </LoadingErrorContainer>
    );
  }

  if (
    !effectivePrimaryPracticeId &&
    canViewMultiplePractices &&
    !isEmbeddedUser &&
    !isLoadingVideaPatientId
  ) {
    // For director users, wait until we have the practice ID from the API
    if (isDirectorUser(user) && !directorPracticeId) {
      // Don't redirect yet, wait for the API to set the director practice ID
      return (
        <LoadingErrorContainer>
          <div className="message-container">
            <VideaTypography variant="headingMedium" color={blueBlack630}>
              <Trans>Loading practice information...</Trans>
            </VideaTypography>
            <LinearProgress />
          </div>
        </LoadingErrorContainer>
      );
    }

    setRedirectToSwitchBoard(true);

    isRedirecting.current = true;
    return (
      <Redirect
        to={showOpDashboard ? routes.VIDEA_INSIGHT : routes.SWITCHBOARD}
      />
    );
  }

  if (videaPatientId && isErrorVideaImageId) {
    trackEvent(mixpanelEvents.externalUrlTimeout, {
      reason: 'image id error',
      timeOpen,
    });

    isRedirecting.current = true;
    return <Redirect to={`${routes.ANNOTATOR}/${videaPatientId}`} />;
  }

  if (videaPatientId) {
    setIsExternalUser(true);
    if (videaImageId?.videaImageId) {
      if (videaImageId.estimatedVisitId) {
        setEstimatedVisitId(String(videaImageId.estimatedVisitId));
      }
      const redirectUrl = isEmbeddedUser
        ? `/embedded${routes.ANNOTATOR}/${videaPatientId}/${videaImageId.videaImageId}`
        : `${routes.ANNOTATOR}/${videaPatientId}/${videaImageId.videaImageId}`;

      trackEvent(mixpanelEvents.externalUrlSuccess, {
        videaPatientId,
        videaImageId,
        timeOpen,
      });

      isRedirecting.current = true;
      return <Redirect to={redirectUrl} />;
    }
    if (!practiceImageId) {
      const redirectUrl = isEmbeddedUser
        ? `/embedded${routes.ANNOTATOR}/${videaPatientId}`
        : `${routes.ANNOTATOR}/${videaPatientId}`;

      trackEvent(mixpanelEvents.externalUrlSuccess, {
        videaPatientId,
        timeOpen,
      });

      isRedirecting.current = true;
      return <Redirect to={redirectUrl} />;
    }
  }

  // Don't run error case if we're already redirecting
  if (isRedirecting.current) {
    return null;
  }

  // Default case: redirect to error page if we can't handle the request

  const errorUrl = isEmbeddedUser
    ? `/embedded${routes.PATIENT_MATCH_ERROR}`
    : routes.PATIENT_MATCH_ERROR;

  trackEvent(mixpanelEvents.externalUrlTimeout, {
    reason: 'unexpected state',
    timeOpen,
  });

  isRedirecting.current = true;
  return <Redirect to={errorUrl} />;
};
